import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { apiGet } from '@/api';
import {
    Server, AlertCircle, CheckCircle2, Clock, Database,
    Lock, Link2, RefreshCw, Shield, Info, ArrowDownToLine, Table2,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────
type ConnectionMode = 'saved' | 'custom';
type WriteMode      = 'insert' | 'replace' | 'upsert';

interface RelayAgent {
    id: string; name: string; description?: string;
    status: 'ACTIVE' | 'IDLE' | 'PENDING' | 'REVOKED' | string;
}

interface DataSourceOption { id: string; name: string; type: number; }
interface TableOption      { name: string; schema?: string; }

interface RelaySinkConfigProps {
    node: any;
    onChangeData: (key: string | Record<string, any>, value?: any) => void;
    inputColumns?: { name: string; type?: string }[];
}

const DB_TYPE_LABELS: Record<number, string> = {
    0: 'PostgreSQL', 1: 'MySQL', 2: 'SQL Server', 3: 'Oracle',
    4: 'Snowflake',  5: 'BigQuery', 6: 'Redshift',  7: 'Azure SQL',
    8: 'Synapse',    13: 'DuckDB',
};
const DB_TYPES = new Set(Object.keys(DB_TYPE_LABELS).map(Number));

const STATUS_STYLES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2 }> = {
    ACTIVE:  { variant: 'default',     icon: CheckCircle2 },
    IDLE:    { variant: 'secondary',   icon: Clock },
    PENDING: { variant: 'outline',     icon: Clock },
    REVOKED: { variant: 'destructive', icon: AlertCircle },
};

const WRITE_MODE_INFO: Record<WriteMode, { label: string; hint: string }> = {
    insert:  { label: 'Insert (Append)',    hint: 'Append new rows — never modifies existing rows' },
    replace: { label: 'Replace (Truncate)', hint: 'Truncate target table then insert all rows' },
    upsert:  { label: 'Upsert (Merge)',     hint: 'Update matching rows by key, insert new ones' },
};

// ── Component ─────────────────────────────────────────────────────────────────
export const RelaySinkConfig = ({ node, onChangeData, inputColumns = [] }: RelaySinkConfigProps) => {
    const data = node.data || {};

    const [agents, setAgents]               = useState<RelayAgent[]>([]);
    const [dataSources, setDataSources]     = useState<DataSourceOption[]>([]);
    const [tables, setTables]               = useState<TableOption[]>([]);
    const [agentLoading, setAgentLoading]   = useState(false);
    const [dsLoading, setDsLoading]         = useState(false);
    const [tableLoading, setTableLoading]   = useState(false);
    const [agentError, setAgentError]       = useState<string | null>(null);
    const [dsError, setDsError]             = useState<string | null>(null);
    const [tableError, setTableError]       = useState<string | null>(null);
    // fallback to manual text entry when schema fetch fails / custom URL mode
    const [tableManual, setTableManual]     = useState(false);

    const connectionMode: ConnectionMode = data.connectionMode || 'saved';
    const writeMode: WriteMode           = data.writeMode || 'insert';
    const pdColumns: string[]            = data.pdColumns || [];
    const writeColumns: string[]         = data.writeColumns || [];

    const safeColumns    = inputColumns.filter(c => !pdColumns.includes(c.name));
    const selectedAgent  = agents.find(a => a.id === data.agentId);
    const selectedSource = dataSources.find(ds => ds.id === data.dataSourceId);

    // ── Fetch relay agents ────────────────────────────────────────────────────
    useEffect(() => {
        setAgentLoading(true);
        apiGet('/relay/agents')
            .then((res: any) => Array.isArray(res) && setAgents(res.filter((a: RelayAgent) => a.status !== 'REVOKED')))
            .catch(() => setAgentError('Could not load relay agents.'))
            .finally(() => setAgentLoading(false));
    }, []);

    // ── Fetch saved data-sources ──────────────────────────────────────────────
    const fetchDataSources = () => {
        setDsLoading(true); setDsError(null);
        apiGet('/datasources')
            .then((res: any) => Array.isArray(res) && setDataSources(
                res.filter((ds: any) => DB_TYPES.has(ds.type))
                   .map((ds: any) => ({ id: ds.id, name: ds.name, type: ds.type }))
            ))
            .catch(() => setDsError('Could not load saved connections.'))
            .finally(() => setDsLoading(false));
    };

    useEffect(() => { if (connectionMode === 'saved') fetchDataSources(); }, [connectionMode]);

    // ── Fetch tables for selected datasource ──────────────────────────────────
    const fetchTables = (dsId: string) => {
        if (!dsId) return;
        setTableLoading(true);
        setTableError(null);
        setTableManual(false);
        setTables([]);
        apiGet(`/datasources/${dsId}/schema`)
            .then((res: any) => {
                const rows: TableOption[] = Array.isArray(res)
                    ? res.map((t: any) => ({
                        name:   t.name || t.table_name || String(t),
                        schema: t.schema || t.table_schema,
                    })).filter(t => t.name)
                    : [];
                if (rows.length === 0) {
                    setTableError('No tables returned. Enter the table name manually.');
                    setTableManual(true);
                } else {
                    setTables(rows);
                }
            })
            .catch(() => {
                setTableError('Could not load tables. Enter the name manually.');
                setTableManual(true);
            })
            .finally(() => setTableLoading(false));
    };

    // Auto-fetch tables when datasource changes
    useEffect(() => {
        if (connectionMode === 'saved' && data.dataSourceId) {
            fetchTables(data.dataSourceId);
        } else {
            setTables([]);
            setTableError(null);
            setTableManual(connectionMode === 'custom');
        }
    }, [data.dataSourceId, connectionMode]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleAgentChange = (agentId: string) => {
        const agent = agents.find(a => a.id === agentId);
        onChangeData({ agentId, agentName: agent?.name || '' });
    };

    const handleModeChange = (mode: ConnectionMode) => {
        onChangeData({ connectionMode: mode, dataSourceId: undefined, dataSourceName: undefined, targetTable: undefined });
        setTables([]); setTableError(null); setTableManual(mode === 'custom');
        if (mode === 'saved') fetchDataSources();
    };

    const handleDataSourceChange = (dsId: string) => {
        const ds = dataSources.find(d => d.id === dsId);
        onChangeData({ dataSourceId: dsId, dataSourceName: ds?.name || '', dataSourceType: ds?.type, targetTable: undefined });
        fetchTables(dsId);
    };

    const handleWriteColumnToggle = (colName: string) => {
        const current = new Set(writeColumns);
        if (current.has(colName)) current.delete(colName); else current.add(colName);
        onChangeData('writeColumns', Array.from(current));
    };

    const isReady = data.agentId && (data.dataSourceId || data.customConnectionUrl) && data.targetTable;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">

            {/* ── Security banner ─────────────────────────────────────────── */}
            <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                <Shield className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                    <p className="text-[11px] font-semibold text-emerald-800">Write-Back Security</p>
                    <p className="text-[10px] text-emerald-700 leading-relaxed">
                        Only <strong>new computed columns</strong> (ML scores, enrichments) are written back.
                        PD columns are automatically excluded — they were never stored in the platform.
                    </p>
                </div>
            </div>

            {/* ── 1. Agent ────────────────────────────────────────────────── */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Server className="h-3.5 w-3.5 text-emerald-500" /> Relay Agent
                </label>
                {agentError && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />{agentError}
                    </div>
                )}
                <Select value={data.agentId || ''} onValueChange={handleAgentChange} disabled={agentLoading || agents.length === 0}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder={agentLoading ? 'Loading agents…' : agents.length === 0 ? 'No active agents' : 'Select a Relay Agent…'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                        {agents.map(agent => {
                            const style = STATUS_STYLES[agent.status] || STATUS_STYLES['IDLE'];
                            const Icon  = style.icon;
                            return (
                                <SelectItem key={agent.id} value={agent.id}>
                                    <div className="flex items-center gap-2">
                                        <Icon className="h-3 w-3 text-gray-400 shrink-0" />
                                        <span>{agent.name}</span>
                                        <Badge variant={style.variant} className="text-[9px] px-1 py-0 ml-auto">{agent.status}</Badge>
                                    </div>
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
                {selectedAgent && (
                    <div className="p-2 bg-emerald-50 border border-emerald-100 rounded-lg flex items-center justify-between">
                        <span className="text-[11px] font-medium text-emerald-900">{selectedAgent.name}</span>
                        <Badge variant={STATUS_STYLES[selectedAgent.status]?.variant || 'secondary'} className="text-[9px]">
                            {selectedAgent.status}
                        </Badge>
                    </div>
                )}
            </div>

            {/* ── 2. Database Connection ───────────────────────────────────── */}
            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Database className="h-3.5 w-3.5 text-blue-500" /> Target Database Connection
                </label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs font-medium">
                    <button type="button" onClick={() => handleModeChange('saved')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 transition-colors ${connectionMode === 'saved' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        <Database className="h-3 w-3" /> Saved Connection
                    </button>
                    <button type="button" onClick={() => handleModeChange('custom')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 border-l border-gray-200 transition-colors ${connectionMode === 'custom' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        <Link2 className="h-3 w-3" /> Custom URL
                    </button>
                </div>

                {connectionMode === 'saved' && (
                    <div className="space-y-2">
                        {dsError && <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700"><AlertCircle className="h-3.5 w-3.5" />{dsError}</div>}
                        <div className="flex gap-2">
                            <Select value={data.dataSourceId || ''} onValueChange={handleDataSourceChange} disabled={dsLoading}>
                                <SelectTrigger className="w-full text-sm">
                                    <SelectValue placeholder={dsLoading ? 'Loading…' : dataSources.length === 0 ? 'No DB sources' : 'Select connection…'} />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                    {dataSources.map(ds => (
                                        <SelectItem key={ds.id} value={ds.id}>
                                            <div className="flex items-center gap-2 w-full">
                                                <Database className="h-3 w-3 text-gray-400 shrink-0" />
                                                <span className="flex-1">{ds.name}</span>
                                                <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{DB_TYPE_LABELS[ds.type] || `Type ${ds.type}`}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <button type="button" onClick={fetchDataSources} disabled={dsLoading}
                                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors shrink-0">
                                <RefreshCw className={`h-3.5 w-3.5 ${dsLoading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                        {selectedSource && (
                            <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-100 rounded-lg">
                                <Lock className="h-3 w-3 text-green-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] font-medium text-green-800">{selectedSource.name}</p>
                                    <p className="text-[10px] text-green-600">{DB_TYPE_LABELS[selectedSource.type]} · Credentials fetched securely at runtime</p>
                                </div>
                                <Badge className="text-[9px] shrink-0 bg-green-100 text-green-700 border-green-200 hover:bg-green-100">🔒 Secure</Badge>
                            </div>
                        )}
                        <p className="text-[10px] text-gray-400 flex items-start gap-1">
                            <Lock className="h-3 w-3 shrink-0 mt-0.5 text-green-500" />
                            Credentials are <strong>never stored</strong> in the flow.
                        </p>
                    </div>
                )}

                {connectionMode === 'custom' && (
                    <div className="space-y-2">
                        <input type="password" className="w-full text-sm px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-400 bg-amber-50 font-mono"
                            value={data.customConnectionUrl || ''} onChange={(e) => onChangeData('customConnectionUrl', e.target.value)}
                            placeholder="mysql+pymysql://user:pass@host/db" />
                        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertCircle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-[10px] text-amber-700"><strong>Less secure:</strong> Use Saved Connection for production.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* ── 3. Target Table ──────────────────────────────────────────── */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Table2 className="h-3.5 w-3.5 text-emerald-500" /> Target Table
                </label>

                {/* Loading */}
                {tableLoading && (
                    <div className="flex items-center gap-2 p-2.5 border border-gray-200 rounded-lg bg-gray-50 text-xs text-gray-500">
                        <RefreshCw className="h-3.5 w-3.5 animate-spin text-emerald-500" />
                        Loading tables from database…
                    </div>
                )}

                {/* Error / fallback notice */}
                {!tableLoading && tableError && (
                    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        <span className="flex-1">{tableError}</span>
                        {data.dataSourceId && (
                            <button type="button" onClick={() => fetchTables(data.dataSourceId)}
                                className="text-emerald-600 hover:underline font-medium shrink-0">
                                Retry
                            </button>
                        )}
                    </div>
                )}

                {/* Dropdown — tables loaded successfully */}
                {!tableLoading && !tableManual && tables.length > 0 && (
                    <div className="flex gap-2">
                        <Select value={data.targetTable || ''} onValueChange={(v) => onChangeData('targetTable', v)}>
                            <SelectTrigger className="w-full text-sm font-mono">
                                <SelectValue placeholder="Select target table…" />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                                {tables.map(t => {
                                    const display = t.schema ? `${t.schema}.${t.name}` : t.name;
                                    return (
                                        <SelectItem key={display} value={display}>
                                            <div className="flex items-center gap-2">
                                                <Table2 className="h-3 w-3 text-gray-400 shrink-0" />
                                                <span className="font-mono text-xs">{display}</span>
                                            </div>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                        <button type="button" onClick={() => data.dataSourceId && fetchTables(data.dataSourceId)}
                            disabled={tableLoading} title="Refresh table list"
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors shrink-0">
                            <RefreshCw className={`h-3.5 w-3.5 ${tableLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                )}

                {/* Manual text input — custom URL mode OR schema fetch failed */}
                {!tableLoading && (tableManual || connectionMode === 'custom') && (
                    <input type="text"
                        className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:border-emerald-500 bg-gray-50 focus:bg-white transition-colors font-mono"
                        value={data.targetTable || ''}
                        onChange={(e) => onChangeData('targetTable', e.target.value)}
                        placeholder="e.g. dbo.FactCharge or public.predictions"
                    />
                )}

                {/* No connection selected yet */}
                {!tableLoading && !data.dataSourceId && !data.customConnectionUrl && (
                    <p className="text-[10px] text-gray-400 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Select a database connection above to see available tables.
                    </p>
                )}

                <p className="text-[10px] text-gray-400">The table must already exist in the on-premise database.</p>
            </div>

            {/* ── 4. Write Mode ───────────────────────────────────────────── */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Write Mode</label>
                <Select value={writeMode} onValueChange={(v) => onChangeData('writeMode', v)}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                        {(Object.entries(WRITE_MODE_INFO) as [WriteMode, { label: string; hint: string }][]).map(([val, info]) => (
                            <SelectItem key={val} value={val}>
                                <div>
                                    <div className="text-xs font-medium">{info.label}</div>
                                    <div className="text-[10px] text-gray-400">{info.hint}</div>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {writeMode === 'upsert' && (
                    <div className="space-y-1.5 pt-1">
                        <label className="text-[11px] font-medium text-gray-600">Upsert Key Column(s)</label>
                        <input type="text"
                            className="w-full text-sm px-3 py-2 border border-emerald-200 rounded-lg focus:border-emerald-500 bg-emerald-50 focus:bg-white font-mono"
                            value={(data.upsertKey || []).join(', ')}
                            onChange={(e) => {
                                const keys = e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean);
                                onChangeData('upsertKey', keys);
                            }}
                            placeholder="e.g. PatientID, EncounterID"
                        />
                        <p className="text-[10px] text-gray-400">
                            Column(s) used to match existing rows. Must be <strong>passthrough columns</strong> (not masked/hashed).
                        </p>
                    </div>
                )}
            </div>

            {/* ── 5. Write Columns ────────────────────────────────────────── */}
            {inputColumns.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                            Columns to Write
                        </label>
                        <div className="flex gap-2">
                            <button type="button" className="text-[10px] text-emerald-600 hover:underline"
                                onClick={() => onChangeData('writeColumns', safeColumns.map(c => c.name))}>
                                Select All Safe
                            </button>
                            <button type="button" className="text-[10px] text-gray-400 hover:underline"
                                onClick={() => onChangeData('writeColumns', [])}>
                                Clear
                            </button>
                        </div>
                    </div>
                    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                        <div className="grid grid-cols-[1fr_auto_auto] px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[10px] font-semibold text-gray-500 uppercase tracking-wider gap-2">
                            <span>Column</span><span>Type</span><span className="w-12 text-center">Write</span>
                        </div>
                        <div className="max-h-[220px] overflow-y-auto divide-y divide-gray-50">
                            {inputColumns.map(col => {
                                const isPD      = pdColumns.includes(col.name);
                                const isChecked = writeColumns.includes(col.name);
                                return (
                                    <div key={col.name}
                                        className={`grid grid-cols-[1fr_auto_auto] items-center px-3 py-1.5 gap-2 ${isPD ? 'bg-red-50 opacity-60' : 'bg-white hover:bg-gray-50'}`}>
                                        <span className={`text-[11px] font-mono truncate ${isPD ? 'text-red-600 line-through' : 'text-slate-700'}`}>{col.name}</span>
                                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 rounded shrink-0">{col.type || '—'}</span>
                                        <div className="w-12 flex justify-center">
                                            {isPD ? (
                                                <span className="flex items-center gap-0.5 bg-red-100 text-red-600 text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                                                    <Lock size={8} /> PD
                                                </span>
                                            ) : (
                                                <input type="checkbox" checked={isChecked}
                                                    onChange={() => handleWriteColumnToggle(col.name)}
                                                    className="h-3.5 w-3.5 accent-emerald-500 cursor-pointer" />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-[10px] text-gray-500">{inputColumns.length} columns total</span>
                            <div className="flex gap-3">
                                {pdColumns.length > 0 && (
                                    <span className="text-[10px] text-red-600 font-semibold flex items-center gap-1">
                                        <Lock size={10} /> {pdColumns.length} PD — blocked
                                    </span>
                                )}
                                <span className="text-[10px] text-emerald-700 font-semibold">{writeColumns.length} selected</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
                        <Info className="h-3 w-3 text-blue-500 shrink-0 mt-0.5" />
                        <p className="text-[10px] text-blue-700 leading-relaxed">
                            <strong>PD columns are greyed out</strong> — they were masked/hashed on ingest and must not be written back.
                            Only check columns that represent <strong>new computed data</strong> (e.g. ML scores, risk flags, AI categories).
                        </p>
                    </div>
                </div>
            )}

            {inputColumns.length === 0 && (
                <div className="p-3 bg-gray-50 border border-gray-100 rounded-lg text-center">
                    <p className="text-[10px] text-gray-400">Connect a source node to see available columns for write-back.</p>
                </div>
            )}

            {/* ── 6. Ready summary ────────────────────────────────────────── */}
            {isReady && (
                <div className="p-2.5 bg-emerald-50 border border-emerald-100 rounded-lg space-y-1">
                    <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">✅ Ready to Write</p>
                    <div className="text-[10px] text-gray-600 space-y-0.5">
                        <div>Agent: <span className="font-medium text-gray-900">{data.agentName || data.agentId}</span></div>
                        <div>Connection: {connectionMode === 'saved'
                            ? <span className="font-medium text-gray-900">{data.dataSourceName} <span className="text-green-600">🔒 secure</span></span>
                            : <span className="text-amber-700 font-medium">Custom URL ⚠️</span>}
                        </div>
                        <div>Target: <span className="font-mono font-medium text-gray-900">{data.targetTable}</span></div>
                        <div>Write mode: <span className="font-medium text-gray-900">{WRITE_MODE_INFO[writeMode]?.label || writeMode}</span></div>
                        {writeColumns.length > 0 && (
                            <div>Writing: <span className="font-mono text-emerald-700">{writeColumns.slice(0, 4).join(', ')}{writeColumns.length > 4 ? ` +${writeColumns.length - 4} more` : ''}</span></div>
                        )}
                        {pdColumns.length > 0 && (
                            <div className="flex items-center gap-1 text-red-600">
                                <Lock size={10} /> {pdColumns.length} PD column{pdColumns.length > 1 ? 's' : ''} excluded automatically
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RelaySinkConfig;
